import * as React from 'react';
import {
  $createLinkNode,
  $isLinkNode,
  $toggleLink,
  formatUrl,
} from '@lexical/link';
import {
  $createTextNode,
  $findMatchingParent,
  $getSelection,
  $isRangeSelection,
  $setSelection,
} from 'lexical';
import type { InputRef } from 'antd';
import type { LexicalEditor } from 'lexical';
import type {
  ToolbarLinkController,
  ToolbarSelectionContext,
} from './types';

export function useToolbarLink(
  editor: LexicalEditor,
  selection: ToolbarSelectionContext
): ToolbarLinkController {
  const [isEditorVisible, setIsEditorVisible] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');
  const [textValue, setTextValue] = React.useState('');
  const inputRef = React.useRef<InputRef>(null);

  React.useEffect(() => {
    if (!isEditorVisible) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditorVisible]);

  const openEditor = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const currentSelection = $getSelection();

      if ($isRangeSelection(currentSelection)) {
        const hasSelectedText = currentSelection.getTextContent().trim() !== '';
        const anchorNode = currentSelection.anchor.getNode();
        const linkNode = $findMatchingParent(anchorNode, $isLinkNode);

        selection.savedSelectionRef.current = currentSelection.clone();
        setTextValue(hasSelectedText ? '' : linkNode?.getTextContent() ?? '');
      } else {
        selection.savedSelectionRef.current = null;
        setTextValue('');
      }
    });

    setEditValue(selection.currentLinkUrl || 'https://');
    setIsEditorVisible(true);
  }, [editor, selection]);

  const submit = React.useCallback(() => {
    const normalizedUrl = editValue.trim();
    const formattedUrl = normalizedUrl === '' ? '' : formatUrl(normalizedUrl);
    const normalizedText = textValue.trim();

    editor.update(() => {
      if (selection.savedSelectionRef.current) {
        $setSelection(selection.savedSelectionRef.current);
      }

      const currentSelection = $getSelection();

      if ($isRangeSelection(currentSelection)) {
        const hasSelectedText = currentSelection.getTextContent().trim() !== '';

        if (!hasSelectedText) {
          const anchorNode = currentSelection.anchor.getNode();
          const linkNode = $findMatchingParent(anchorNode, $isLinkNode);
          const existingLinkText = linkNode?.getTextContent().trim() ?? '';
          const nextLinkText = normalizedText || existingLinkText;

          if (linkNode) {
            $toggleLink(formattedUrl === '' ? null : formattedUrl);

            if (
              formattedUrl !== '' &&
              normalizedText !== '' &&
              normalizedText !== existingLinkText
            ) {
              linkNode.clear();
              linkNode.append($createTextNode(normalizedText));
              linkNode.selectEnd();
            }
          } else if (formattedUrl !== '' && nextLinkText !== '') {
            const nextLinkNode = $createLinkNode(formattedUrl);
            nextLinkNode.append($createTextNode(nextLinkText));
            currentSelection.insertNodes([nextLinkNode]);
            nextLinkNode.selectEnd();
          } else {
            return;
          }
        } else {
          $toggleLink(formattedUrl === '' ? null : formattedUrl);
        }

        selection.savedSelectionRef.current = currentSelection.clone();
      }
    });

    if (typeof globalThis.window !== 'undefined') {
      globalThis.window.requestAnimationFrame(() => {
        editor.focus();
      });
    }

    setIsEditorVisible(false);
  }, [editValue, editor, selection, textValue]);

  const remove = React.useCallback(() => {
    editor.update(() => {
      if (selection.savedSelectionRef.current) {
        $setSelection(selection.savedSelectionRef.current);
      }

      const currentSelection = $getSelection();

      if ($isRangeSelection(currentSelection)) {
        $toggleLink(null);
        selection.savedSelectionRef.current = currentSelection.clone();
      }
    });

    if (typeof globalThis.window !== 'undefined') {
      globalThis.window.requestAnimationFrame(() => {
        editor.focus();
      });
    }

    setEditValue('');
    setIsEditorVisible(false);
  }, [editor, selection]);

  return {
    editValue,
    inputRef,
    isEditorVisible,
    openEditor,
    remove,
    selectionHasText: selection.selectionHasText,
    setEditValue,
    setIsEditorVisible,
    setTextValue,
    submit,
    textValue,
    url: selection.currentLinkUrl,
  };
}
